import { lazy, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { LazyCanvas } from "@/components/three/LazyCanvas";
import { BeforeAfter } from "./BeforeAfter";
import { openTool } from "./Nav";
import { scrollToId } from "@/lib/scroll";

const CameraScene = lazy(() => import("@/components/three/CameraScene"));
const EASE = [0.2, 0.8, 0.2, 1];
const CHECKS = ["Photos and videos", "Cars, trucks and bikes", "Failed swaps refunded"];

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.06em]">
    <motion.span className="block" initial={{ y: "40%", opacity: 0.4 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 1, delay, ease: EASE }}>
      {children}
    </motion.span>
  </span>
);


const HeroCards = () => (
  <div className="relative">
    <div className="relative rounded-2xl ring-1 ring-white/15 shadow-[0_40px_120px_-30px_rgba(220,224,230,0.35)] overflow-hidden">
      <BeforeAfter before="/images/ebike-before.jpg" after="/images/ebike-after.jpg" labels={["Original", "Background Swap"]} alt="Dirt bike"
        testId="hero-before-after" start={52} className="relative aspect-[4/3]" />
    </div>
    <p className="mt-3 text-xs text-slate-400 text-center">Drag to compare</p>
    <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 -bottom-28 h-40 w-40 lg:h-44 lg:w-44 hidden md:block">
      <div className="absolute inset-6 rounded-full bg-white/10 blur-3xl" />
      <LazyCanvas Scene={CameraScene} orbit={false} testId="hero-3d-showcase" className="absolute inset-0" />
    </div>
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const textY = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const cardY = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const cardRot = useTransform(scrollYProgress, [0, 1], [0, -4]);
  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative overflow-hidden pt-[100px]">
      <div className="absolute inset-0 grid-floor opacity-50 pointer-events-none" />
      <div className="absolute -top-40 right-[-10%] h-[620px] w-[620px] rounded-full bg-[#3b4049]/20 blur-[140px] pointer-events-none" />
      <div className="relative max-w-[1440px] mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-10 items-center py-14 lg:py-24 lg:min-h-[calc(100svh-100px)]">
        <motion.div style={{ y: textY }} className="lg:col-span-6">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-7">
            <span className="eyebrow whitespace-nowrap">AI Photo Studio</span>
            <span className="badge-new">New</span>
            <span className="text-xs text-slate-300 whitespace-nowrap">Video Swap is here</span>
          </div>
          <h1 data-testid="hero-headline" className="h-display chrome text-[2.1rem] sm:text-6xl lg:text-5xl xl:text-[4.25rem] whitespace-nowrap">
            <Line delay={0.1}>Change the</Line>
            <Line delay={0.22}>scene. Keep</Line>
            <Line delay={0.34}>the moment.</Line>
          </h1>
          <p data-testid="hero-subtext" className="mt-8 max-w-xl text-base md:text-lg text-slate-300 leading-relaxed">
            Transform your car, truck, bike, or personal photos and videos with custom backgrounds made to completely change the look.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <button data-testid="hero-cta-button" onClick={() => openTool("background")} className="btn-solid px-7 py-4">
              Transform my photo <ArrowRight className="h-4 w-4" />
            </button>
            <button data-testid="hero-pricing-button" onClick={() => scrollToId("pricing")} className="btn-line px-7 py-4">See pricing</button>
          </div>
          <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-300">
            {CHECKS.map((c) => (
              <li key={c} className="flex items-center gap-2"><Check className="h-4 w-4 text-[#d5d9df]" /> {c}</li>
            ))}
          </ul>
        </motion.div>
        <motion.div initial={{ opacity: 0.3, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.2, delay: 0.2, ease: EASE }} className="lg:col-span-6 pb-6 md:pb-28">
          <motion.div style={{ y: cardY, rotate: cardRot }}>
            <HeroCards />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
